import { forwardRef, memo, ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { css, CSSObject, SerializedStyles } from '@emotion/react';
import {
  autoUpdate,
  flip,
  FloatingFocusManager,
  FloatingNode,
  FloatingPortal,
  offset,
  Placement,
  size,
  useClick,
  useDismiss,
  useFloating,
  useFloatingNodeId,
  useInteractions,
  useRole,
} from '@floating-ui/react';
import { isEqual } from 'lodash-es';
import { onlyText } from 'react-children-utilities';
import { useTranslation } from '@frontend/i18n';
import { ComponentProps } from '@frontend/types';
import { theme } from '@frontend/theme';
import {
  BaseFieldProps,
  Button,
  CaretDownIconSmall,
  CheckboxField,
  ChecklistField,
  ChecklistMenuField,
  Chip,
  FieldLayout,
  FieldLayoutWithAction,
  ListFieldProps,
  PopoverMenu,
  SearchField,
  useControlledField,
  useOnClickOutside,
  usePopoverMenu,
  useTooltip,
} from '@frontend/design-system';

const FLOATING_MENU_MAX_HEIGHT = 400;
const FLOATING_MENU_MIN_WIDTH = 220;
const SEARCH_THRESHOLD = 10;

/**
 * Calculates the total number of selectable options across all groups
 */
function calculateTotalValuesCount(groups: GroupOption[]): number {
  return groups.reduce((count, group) => {
    return count + (group.children.length === 0 ? 1 : group.children.length);
  }, 0);
}

/**
 * Filters groups and their children based on search term.
 * Matches both group labels and child option labels.
 */
function filterGroupsBySearchTerm(groups: GroupOption[], searchTerm: string): GroupOption[] {
  if (!searchTerm) return groups;

  const lowerSearchTerm = searchTerm.toLowerCase();

  return groups
    .map((group) => {
      const groupLabelText = typeof group.label === 'string' ? group.label : group.labelText ?? onlyText(group.label);
      const groupMatches = groupLabelText.toLowerCase().includes(lowerSearchTerm);

      if (group.children.length === 0) {
        return groupMatches ? group : null;
      }

      const filteredChildren = group.children.filter((child) => {
        const childLabelText = typeof child.label === 'string' ? child.label : child.labelText ?? onlyText(child.label);
        return groupMatches || childLabelText.toLowerCase().includes(lowerSearchTerm);
      });

      if (filteredChildren.length === 0) return null;

      return { ...group, children: filteredChildren };
    })
    .filter((group): group is GroupOption => group !== null);
}

/**
 * Calculates selection states for all groups
 */
function calculateGroupSelectionStates<T extends string>(
  groups: GroupOption[],
  selectedValues: T[]
): Map<string, GroupSelectionState> {
  const states = new Map<string, GroupSelectionState>();

  groups.forEach((group) => {
    const hasChildren = group.children.length > 0;

    if (hasChildren) {
      const enabledChildren = group.children.filter((child) => !child.disabled);
      const enabledChildValues = enabledChildren.map((child) => child.value as T);
      const selectedCount = enabledChildValues.filter((childValue) => selectedValues.includes(childValue)).length;
      const totalCount = enabledChildValues.length;

      states.set(group.value, {
        allSelected: totalCount > 0 && selectedCount === totalCount,
        hasChildren: true,
        isIndeterminate: selectedCount > 0 && selectedCount < totalCount,
        selectedCount,
        totalCount,
      });
    } else {
      const isGroupSelected = selectedValues.includes(group.value as T);

      states.set(group.value, {
        allSelected: isGroupSelected,
        hasChildren: false,
        isIndeterminate: false,
        selectedCount: isGroupSelected ? 1 : 0,
        totalCount: 1,
      });
    }
  });

  return states;
}

/**
 * Gets all non-disabled selectable values from groups.
 */
function getAllSelectableValues<T extends string>(groups: GroupOption[]): T[] {
  const selectableValues: T[] = [];

  groups.forEach((group) => {
    if (group.children.length === 0) {
      selectableValues.push(group.value as T);
      return;
    }

    group.children.forEach((child) => {
      // Include locked children (they're selectable via group toggle) but exclude permanently disabled ones
      if (!child.disabled) {
        selectableValues.push(child.value as T);
      }
    });
  });

  const uniqueValues = new Set<string>();
  return selectableValues.filter((selectableValue) => {
    if (uniqueValues.has(selectableValue)) {
      return false;
    }
    uniqueValues.add(selectableValue);
    return true;
  });
}

/**
 * Creates a mapping of option values to their display labels.
 */
function createValueToLabelMap(groups: GroupOption[]): Record<string, string> {
  const map: Record<string, string> = {};

  groups.forEach((group) => {
    // Always map the group value itself (needed for displayValue group chips)
    map[group.value] = typeof group.label === 'string' ? group.label : group.labelText ?? onlyText(group.label);
    if (group.children.length === 0) {
      // leaf group already mapped above
    } else {
      group.children.forEach((child) => {
        map[child.value] = typeof child.label === 'string' ? child.label : child.labelText ?? onlyText(child.label);
      });
    }
  });

  return map;
}

/**
 * Custom hook that manages search functionality for grouped checklists.
 */
function useGroupedChecklistSearch(groups: GroupOption[]) {
  const [searchTerm, setSearchTerm] = useState('');

  const totalValuesCount = useMemo(() => calculateTotalValuesCount(groups), [groups]);
  const showSearchBar = totalValuesCount > SEARCH_THRESHOLD;
  const filteredGroups = useMemo(() => filterGroupsBySearchTerm(groups, searchTerm), [groups, searchTerm]);

  const searchFieldProps = useControlledField({
    onChange: (newValue: string) => setSearchTerm(newValue),
    type: 'text',
    value: searchTerm,
  });

  const clearSearch = useCallback(() => setSearchTerm(''), []);

  return {
    searchTerm,
    setSearchTerm,
    showSearchBar,
    filteredGroups,
    searchFieldProps,
    clearSearch,
  };
}

const SearchBarField = memo(
  ({
    searchFieldProps,
    cssStyle,
  }: {
    searchFieldProps: BaseFieldProps<string, '', HTMLInputElement>;
    cssStyle: SerializedStyles;
  }) => (
    <div css={cssStyle}>
      <SearchField
        {...searchFieldProps}
        name='search'
        onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
          if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === 'Tab') {
            return;
          }
          e.stopPropagation();
        }}
      />
    </div>
  )
);

SearchBarField.displayName = 'SearchBarField';

type GroupedChildOption = {
  disabled?: boolean;
  /**
   * Visually disables the child (individual clicks do nothing) but still counts toward
   * the group's allSelected state and is included in group-level toggle operations.
   * Use this for dynamically locked items (e.g. selection-limit reached, group all-selected)
   * rather than permanently excluded items (use `disabled` for those).
   */
  locked?: boolean;
  tooltipText?: string;
  label: ReactNode;
  labelText?: string;
  value: string;
};

export type GroupOption = {
  children: GroupedChildOption[];
  hideLabel?: boolean;
  labelText?: string;
  label: ReactNode;
  value: string;
  trackingId?: string;
};

type GroupedCheckListDropdownMenuProps<T extends string = string> = {
  disabled?: boolean;
  fieldContainerCss?: SerializedStyles;
  floatingStyleOverrides?: React.CSSProperties;
  groups: GroupOption[];
  label: string;
  name: string;
  onChange: (selected: T[]) => void;
  placement?: Placement;
  placeholder?: string;
  showLabel?: boolean;
  trackingIdBase?: string;
  value?: T[];
  showOverflowTooltip?: boolean;
  /** Constrains the scrollable content (parent groups) area to this height in px. */
  scrollableContentMaxHeight?: number;
  /** Fixed pixel width for the field box. Also used to compute per-chip max-width to prevent chip overflow. */
  width?: number;
  /**
   * Called whenever the internal (pre-Apply) selection changes.
   * Use this to get live selection updates without waiting for the Apply button.
   */
  onInternalSelectionChange?: (selection: T[]) => void;
  /**
   * When `false`, every checkbox change is committed to `onChange` immediately and the Apply button
   * is hidden. Use this when the parent already has its own Apply/submit step (e.g. a tray filter).
   * Defaults to `true`.
   */
  showApplyButton?: boolean;
  /**
   * Optional icon rendered as a start adornment inside the field.
   * Only shown when the menu is closed and no chips are selected.
   */
  renderIcon?: () => React.ReactNode;
};

type GroupSelectionState = {
  allSelected: boolean;
  hasChildren: boolean;
  isIndeterminate: boolean;
  selectedCount: number;
  totalCount: number;
};

type GroupCheckboxProps = {
  disabled?: boolean;
  indeterminate: boolean;
  isChecked: boolean;
  label: ReactNode;
  name: string;
  onChange: (value: boolean) => void;
  trackingId?: string;
};

type ChipInputFieldWrapperProps = {
  chipMaxWidth?: number;
  keyToLabelMap: Record<string, string>;
  onRemoveClick: (chipValue: string) => void;
  selectedKeys: string[];
  showOverflowTooltip?: boolean;
};

type ChipDropdownMenuCustomStyles = {
  applyButton?: CSSObject;
  checkListWrapper?: CSSObject;
  chipDropdownFilter?: CSSObject;
  popoverMenuWrapper?: CSSObject;
  topBar?: CSSObject;
};

type GroupedChecklistChipDropdownMenuProps<T extends string = string> = {
  customStyles?: ChipDropdownMenuCustomStyles;
  defaultValue?: T[];
  disabled?: boolean;
  groups: GroupOption[];
  label: string;
  onChange: (selected: T[]) => void;
  /**
   * Called whenever the internal (pre-Apply) selection changes.
   * Use this to get live selection updates without waiting for the Apply button.
   */
  onInternalSelectionChange?: (selection: T[]) => void;
  renderIcon?: (isFilterActive: boolean, isOpen?: boolean) => React.ReactNode;
  trackingId?: string;
  value?: T[];
  /** Constrains the scrollable content (parent groups) area to this height in px. */
  scrollableContentMaxHeight?: number;
};

const GroupCheckbox = memo(
  ({ disabled, indeterminate, isChecked, label, name, onChange, trackingId }: GroupCheckboxProps) => {
    const checkboxFieldProps = useControlledField({
      onChange: (newValue: boolean) => {
        onChange(newValue);
      },
      type: 'checkbox',
      value: isChecked,
    });

    return (
      <CheckboxField
        {...checkboxFieldProps}
        css={styles.groupCheckbox}
        disabled={disabled}
        indeterminate={indeterminate}
        label={label}
        labelPlacement='right'
        name={name}
        trackingId={trackingId}
      />
    );
  }
);

GroupCheckbox.displayName = 'GroupCheckbox';

const LockedChildOption = memo(
  ({
    child,
    hideGroupLabel,
    tooltipText,
    trackingIdBase,
  }: {
    child: GroupedChildOption;
    hideGroupLabel: boolean;
    tooltipText?: string;
    trackingIdBase?: string;
  }) => {
    const { Tooltip, triggerProps, tooltipProps } = useTooltip({ placement: 'top' });

    return (
      <div css={styles.childOption(hideGroupLabel)} {...(tooltipText ? triggerProps : {})}>
        <ChecklistField.Option
          css={styles.childCheckbox}
          disabled
          name={child.value}
          trackingId={`${trackingIdBase}-option-${child.value.toLowerCase()}`}
        >
          {child.label}
        </ChecklistField.Option>
        {tooltipText && <Tooltip {...tooltipProps}>{tooltipText}</Tooltip>}
      </div>
    );
  }
);

LockedChildOption.displayName = 'LockedChildOption';

const ChipInputFieldWrapper = memo(
  forwardRef<HTMLDivElement, ChipInputFieldWrapperProps>(
    ({ chipMaxWidth, selectedKeys, keyToLabelMap, onRemoveClick, showOverflowTooltip, ...props }, ref) => {
      return (
        <div ref={ref} {...props} style={styles.chipInputContainer}>
          <div
            css={[
              styles.chipInputWrapper,
              chipMaxWidth !== undefined &&
                css`
                  .chip {
                    max-width: ${chipMaxWidth}px;
                  }
                `,
            ]}
          >
            <ChecklistMenuField.ChipInput
              keyToLabelMap={keyToLabelMap}
              onRemoveClick={onRemoveClick}
              selectedKeys={selectedKeys}
              showOverflowTooltip={showOverflowTooltip}
              variant='primary'
            />
          </div>
        </div>
      );
    }
  )
);

ChipInputFieldWrapper.displayName = 'ChipInputFieldWrapper';

const GroupedChecklistContent = memo(
  ({
    checklistProps,
    disabled,
    groups,
    groupStates,
    label,
    name,
    onToggleGroup,
    showLabel,
    trackingIdBase,
  }: {
    checklistProps: ListFieldProps;
    disabled?: boolean;
    groups: GroupOption[];
    groupStates: Map<string, GroupSelectionState>;
    label: string;
    name: string;
    onToggleGroup: (group: GroupOption) => void;
    showLabel?: boolean;
    trackingIdBase?: string;
  }) => {
    return (
      <ChecklistField
        {...checklistProps}
        css={styles.checklist(showLabel)}
        disabled={disabled ? true : undefined}
        label={label}
        name={name}
      >
        {groups.map((group) => {
          const state = groupStates.get(group.value);
          const hasChildren = group.children.length > 0;
          const hideGroupLabel = group.hideLabel ?? false;

          return (
            <div css={styles.group} key={group.value}>
              {!hideGroupLabel && (
                <div css={styles.groupHeader(hasChildren)}>
                  <GroupCheckbox
                    disabled={disabled}
                    indeterminate={state?.isIndeterminate ?? false}
                    isChecked={state?.isIndeterminate ? true : state?.allSelected ?? false}
                    label={group.label}
                    name={group.value}
                    onChange={() => onToggleGroup(group)}
                    trackingId={`${trackingIdBase}-option-${group.trackingId ?? group.value.toLowerCase()}`}
                  />
                </div>
              )}

              {hasChildren && (
                <>
                  {group.children.map((child) => {
                    if (child.locked) {
                      return (
                        <LockedChildOption
                          key={child.value}
                          child={child}
                          hideGroupLabel={hideGroupLabel}
                          tooltipText={child.tooltipText}
                          trackingIdBase={trackingIdBase}
                        />
                      );
                    }

                    return (
                      <div css={styles.childOption(hideGroupLabel)} key={child.value}>
                        <ChecklistField.Option
                          css={styles.childCheckbox}
                          disabled={child.disabled || !!child.locked}
                          name={child.value}
                          trackingId={`${trackingIdBase}-option-${child.value.toLowerCase()}`}
                        >
                          {child.label}
                        </ChecklistField.Option>
                      </div>
                    );
                  })}
                </>
              )}
            </div>
          );
        })}
      </ChecklistField>
    );
  }
);

GroupedChecklistContent.displayName = 'GroupedChecklistContent';

export function GroupedCheckListDropdownMenu<T extends string = string>({
  disabled,
  fieldContainerCss,
  floatingStyleOverrides,
  groups,
  label,
  name,
  onChange,
  onInternalSelectionChange,
  placement = 'bottom',
  placeholder = 'Select one',
  renderIcon,
  showApplyButton = true,
  showLabel = false,
  showOverflowTooltip = false,
  scrollableContentMaxHeight,
  trackingIdBase,
  value = [],
  width,
}: GroupedCheckListDropdownMenuProps<T>) {
  const { t } = useTranslation('call-intelligence');

  // Internal (staged) selection — only committed to `onChange` when Apply is clicked
  const [internalValue, setInternalValue] = useState<T[]>(value);

  // Sync internal state when external value changes (e.g. chip removal from the field)
  useEffect(() => {
    setInternalValue(value);
  }, [value]);

  // Notify parent of live (pre-Apply) selection changes so it can react in real-time
  // (e.g. updating locked/disabled states on other checkboxes)
  useEffect(() => {
    onInternalSelectionChange?.(internalValue);
  }, [internalValue, onInternalSelectionChange]);

  // padding (16px) + caret icon + gap (24px) = 40px always reserved
  // "+N more" chip (~60px) + chip gap (8px) = 68px only reserved when multiple items are selected
  const chipMaxWidth = width !== undefined ? width - 40 - (internalValue.length > 1 ? 68 : 0) : undefined;

  // Commits newValue to internalValue. When showApplyButton=false, also calls onChange immediately.
  const handleInternalChange = useCallback(
    (newValue: T[]) => {
      setInternalValue(newValue);
      if (!showApplyButton) {
        onChange(newValue);
      }
    },
    [onChange, showApplyButton]
  );
  const [menuState, setMenuState] = useState({
    isOpen: false,
  });

  const startIcon = renderIcon && !menuState.isOpen && internalValue.length === 0 ? renderIcon() : undefined;

  const { showSearchBar, filteredGroups, searchFieldProps, clearSearch } = useGroupedChecklistSearch(groups);

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => {
      handleInternalChange(newValue as T[]);
    },
    type: 'checklist',
    value: internalValue as string[],
  });

  const floatingNodeId = useFloatingNodeId();

  const floatingConfig = useMemo(
    () => ({
      middleware: [
        offset(placement === 'top' ? 12 : 3),
        flip(),
        size({
          apply({ elements, rects }) {
            Object.assign(elements.floating.style, {
              width: `${rects.reference.width}px`,
            });
          },
        }),
      ],
      nodeId: floatingNodeId,
      onOpenChange: (nextOpen: boolean) => {
        setMenuState({ isOpen: nextOpen });
        if (!nextOpen) {
          clearSearch();
          if (showApplyButton) {
            // Discard staged changes when menu closes without Apply
            setInternalValue(value);
          }
        }
      },
      open: menuState.isOpen,
      placement,
      whileElementsMounted: autoUpdate,
    }),
    [floatingNodeId, menuState.isOpen, placement, clearSearch]
  );

  const { refs, context, floatingStyles } = useFloating<HTMLInputElement>(floatingConfig);

  const { getReferenceProps } = useInteractions([
    useClick(context),
    useRole(context, { role: 'listbox' }),
    useDismiss(context),
  ]);

  const groupSelectionStates = useMemo(
    () => calculateGroupSelectionStates(groups, internalValue),
    [groups, internalValue]
  );

  const leafGroups = useMemo(() => filteredGroups.filter((g) => g.children.length === 0), [filteredGroups]);
  const parentGroups = useMemo(() => filteredGroups.filter((g) => g.children.length > 0), [filteredGroups]);

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      if (!state?.hasChildren) {
        const parentValue = group.value as T;
        const isSelected = internalValue.includes(parentValue);

        handleInternalChange(
          isSelected ? internalValue.filter((v) => v !== parentValue) : [...internalValue, parentValue]
        );
      } else {
        const enabledChildValues = group.children.filter((child) => !child.disabled).map((child) => child.value) as T[];

        if (state?.allSelected) {
          handleInternalChange(internalValue.filter((v) => !enabledChildValues.includes(v)));
        } else {
          const updatedValue = [...internalValue];
          enabledChildValues.forEach((childValue) => {
            if (!updatedValue.includes(childValue)) {
              updatedValue.push(childValue);
            }
          });
          handleInternalChange(updatedValue);
        }
      }
    },
    [groupSelectionStates, internalValue, handleInternalChange]
  );

  const handleApply = useCallback(() => {
    onChange(internalValue);
    setMenuState({ isOpen: false });
    clearSearch();
  }, [onChange, internalValue, clearSearch]);

  const valueToLabelMap = useMemo(() => createValueToLabelMap(groups), [groups]);

  // Auto-collapse chips: for each group whose all children are in `internalValue`,
  // replace the individual child chips with a single group-level chip.
  // Uses internalValue so chips update live as the user makes selections.
  const displayValue = useMemo(() => {
    const collapsedChips: string[] = [];
    let collapsed = false;

    groups.forEach((group) => {
      if (group.children.length === 0) {
        // Leaf group — keep as-is
        if (internalValue.includes(group.value as T)) {
          collapsedChips.push(group.value);
        }
        return;
      }

      const enabledChildren = group.children.filter((c) => !c.disabled);
      const allSelected =
        enabledChildren.length > 0 && enabledChildren.every((c) => internalValue.includes(c.value as T));

      if (allSelected) {
        collapsedChips.push(group.value);
        collapsed = true;
      } else {
        group.children.forEach((c) => {
          if (internalValue.includes(c.value as T)) {
            collapsedChips.push(c.value);
          }
        });
      }
    });

    // Only return a custom displayValue when at least one group was collapsed;
    // otherwise let selectedKeys fall back to `internalValue` unchanged.
    return collapsed ? (collapsedChips as T[]) : undefined;
  }, [groups, internalValue]);

  const handleRemoveChip = useCallback(
    ({ chipValue }: { chipValue: string }) => {
      const removeFrom = (selection: T[]): T[] => {
        if (!selection.includes(chipValue as T)) {
          // Chip represents a collapsed group — remove all its children
          const group = groups.find((g) => g.value === chipValue);
          if (group) {
            const childValues = new Set(group.children.map((c) => c.value));
            return selection.filter((v) => !childValues.has(v)) as T[];
          }
          return selection;
        }
        return selection.filter((v) => v !== chipValue) as T[];
      };

      if (menuState.isOpen) {
        // Menu is open: update staged selection (handleInternalChange will also call onChange if no Apply button)
        handleInternalChange(removeFrom(internalValue));
      } else {
        // Menu is closed: always commit immediately
        onChange(removeFrom(value));
      }
    },
    [onChange, value, internalValue, groups, menuState.isOpen, handleInternalChange]
  );

  const chipInputFieldProps = useMemo(
    () => ({
      chipMaxWidth,
      keyToLabelMap: valueToLabelMap,
      onRemoveClick: (chipValue: string) => handleRemoveChip({ chipValue }),
      selectedKeys: (displayValue ?? internalValue) as string[],
      showOverflowTooltip,
    }),
    [chipMaxWidth, displayValue, handleRemoveChip, internalValue, valueToLabelMap, showOverflowTooltip]
  );

  const handleToggleMenu = useCallback(() => {
    setMenuState((prevState) => ({ isOpen: !prevState.isOpen }));
  }, []);

  const floatingMenuStyles = useMemo(
    () => ({
      ...floatingStyles,
      backgroundColor: theme.colors.white,
      borderRadius: theme.borderRadius.medium,
      boxShadow: theme.shadows.heavy,
      display: 'flex' as const,
      flexDirection: 'column' as const,
      maxHeight: FLOATING_MENU_MAX_HEIGHT,
      minWidth: FLOATING_MENU_MIN_WIDTH,
      paddingBottom: theme.spacing(1),
      zIndex: theme.zIndex.popover,
      ...floatingStyleOverrides,
    }),
    [floatingStyles, floatingStyleOverrides]
  );

  return (
    <FieldLayout
      outerProps={{ 'data-trackingId': trackingIdBase }}
      {...checklistFieldProps}
      {...getReferenceProps()}
      containerCss={[
        styles.fieldContainer,
        width !== undefined &&
          css`
            width: ${width}px;
          `,
        fieldContainerCss,
      ]}
      endAdornment={
        <CaretDownIconSmall
          color='subdued'
          css={[
            css`
              cursor: pointer;
              transition: transform 250ms ease-out;
              pointer-events: ${menuState.isOpen ? 'none' : 'auto'};
            `,

            menuState.isOpen &&
              css`
                transform: rotate(180deg);
              `,
          ]}
          name='alt-caret-down-tiny'
          onClick={handleToggleMenu}
          size={16}
        />
      }
      field={ChipInputFieldWrapper as unknown as ComponentProps<typeof FieldLayoutWithAction>['field']}
      fieldComponentProps={chipInputFieldProps}
      hasPadding={false}
      label={label}
      name={name}
      onChange={() => {}}
      placeholder={placeholder}
      ref={refs.setReference}
      startAdornment={startIcon}
      startAdornmentWidth={startIcon ? 8 : undefined}
      value={value}
      active={menuState.isOpen}
    >
      <FloatingNode id={floatingNodeId}>
        {menuState.isOpen && (
          <FloatingPortal>
            <FloatingFocusManager context={context} initialFocus={0}>
              <div style={floatingMenuStyles} ref={refs.setFloating}>
                {showSearchBar && <SearchBarField searchFieldProps={searchFieldProps} cssStyle={styles.searchBar} />}
                {leafGroups.length > 0 && (
                  <div css={styles.pinnedContent}>
                    <GroupedChecklistContent
                      checklistProps={checklistFieldProps}
                      disabled={disabled}
                      groups={leafGroups}
                      groupStates={groupSelectionStates}
                      label={label}
                      name={`${name}-pinned`}
                      onToggleGroup={handleToggleGroupSelection}
                      showLabel={showLabel}
                      trackingIdBase={trackingIdBase}
                    />
                  </div>
                )}
                {parentGroups.length > 0 && (
                  <div
                    css={[
                      styles.floatingScrollableContent,
                      scrollableContentMaxHeight !== undefined &&
                        css`
                          max-height: ${scrollableContentMaxHeight}px;
                        `,
                    ]}
                  >
                    <GroupedChecklistContent
                      checklistProps={checklistFieldProps}
                      disabled={disabled}
                      groups={parentGroups}
                      groupStates={groupSelectionStates}
                      label={label}
                      name={name}
                      onToggleGroup={handleToggleGroupSelection}
                      showLabel={showLabel}
                      trackingIdBase={trackingIdBase}
                    />
                  </div>
                )}

                {showApplyButton && (
                  <Button
                    css={styles.applyButton}
                    disabled={disabled}
                    onClick={handleApply}
                    trackingId={trackingIdBase ? `${trackingIdBase}-apply-button` : undefined}
                  >
                    {t('Apply')}
                  </Button>
                )}
              </div>
            </FloatingFocusManager>
          </FloatingPortal>
        )}
      </FloatingNode>
    </FieldLayout>
  );
}

export function GroupedChecklistChipDropdownMenu<T extends string = string>({
  customStyles,
  defaultValue = [],
  disabled,
  groups,
  label,
  onChange,
  onInternalSelectionChange,
  renderIcon,
  scrollableContentMaxHeight,
  trackingId,
  value,
}: GroupedChecklistChipDropdownMenuProps<T>) {
  const { t } = useTranslation('call-intelligence');
  const { close, getMenuProps, getTriggerProps, isOpen, open } = usePopoverMenu({
    placement: 'bottom-start',
  });
  const { ref: filterTriggerRef, ...triggerProps } = getTriggerProps();
  const [internalSelection, setInternalSelection] = useState<T[]>(value || []);

  const { showSearchBar, filteredGroups, searchFieldProps, clearSearch } = useGroupedChecklistSearch(groups);

  const outsideClickRef = useRef<HTMLDivElement>(null);
  const isFilterActive = !isEqual([...defaultValue].sort(), [...(value || [])].sort());

  const leftElement = useMemo(() => {
    return renderIcon ? renderIcon(isFilterActive, isOpen) : null;
  }, [renderIcon, isFilterActive, isOpen]);

  useEffect(() => {
    setInternalSelection(value || []);
  }, [value]);

  useEffect(() => {
    onInternalSelectionChange?.(internalSelection);
  }, [internalSelection, onInternalSelectionChange]);

  useEffect(() => {
    if (!isOpen) {
      clearSearch();
    }
  }, [isOpen, clearSearch]);

  const handleCancelSelection = useCallback(() => {
    setInternalSelection(value || []);
    close();
  }, [close, value]);

  useOnClickOutside({
    handler: handleCancelSelection,
    ref: outsideClickRef,
  });

  const handleApplySelection = useCallback(() => {
    onChange(internalSelection);
    close();
  }, [close, internalSelection, onChange]);

  const groupSelectionStates = useMemo(
    () => calculateGroupSelectionStates(groups, internalSelection),
    [groups, internalSelection]
  );

  const leafGroups = useMemo(() => filteredGroups.filter((g) => g.children.length === 0), [filteredGroups]);
  const parentGroups = useMemo(() => filteredGroups.filter((g) => g.children.length > 0), [filteredGroups]);

  const handleToggleGroupSelection = useCallback(
    (group: GroupOption) => {
      const state = groupSelectionStates.get(group.value);

      if (!state?.hasChildren) {
        const groupValue = group.value as T;
        setInternalSelection((previousSelection) =>
          previousSelection.includes(groupValue)
            ? previousSelection.filter((selectedValue) => selectedValue !== groupValue)
            : [...previousSelection, groupValue]
        );
        return;
      }

      // Include locked children (not permanently disabled) so group toggle bypasses individual locks
      const toggleableChildren = group.children.filter((child) => !child.disabled).map((child) => child.value as T);

      setInternalSelection((previousSelection) => {
        if (state.allSelected) {
          return previousSelection.filter((selectedValue) => !toggleableChildren.includes(selectedValue));
        }

        const updatedSelection = [...previousSelection];
        toggleableChildren.forEach((childValue) => {
          if (!updatedSelection.includes(childValue)) {
            updatedSelection.push(childValue);
          }
        });
        return updatedSelection;
      });
    },
    [groupSelectionStates]
  );

  const allSelectableValues = useMemo(() => getAllSelectableValues<T>(groups), [groups]);

  const handleSelectAll = useCallback(() => {
    setInternalSelection(allSelectableValues);
  }, [allSelectableValues]);

  const handleClearAll = useCallback(() => {
    setInternalSelection([]);
  }, []);

  const checklistFieldProps = useControlledField({
    onChange: (newValue) => setInternalSelection(newValue as T[]),
    type: 'checklist',
    value: internalSelection as string[],
  });

  return (
    <div ref={filterTriggerRef}>
      <Chip.DropdownFilter
        {...triggerProps}
        css={{
          gap: theme.spacing(1),
          maxWidth: 'none',
          ...customStyles?.chipDropdownFilter,
        }}
        filterIsActive={isFilterActive}
        leftElement={leftElement}
        onClick={open}
        trackingId={trackingId}
      >
        {label}
      </Chip.DropdownFilter>

      <PopoverMenu {...getMenuProps()}>
        <div css={[styles.checkListWrapper, customStyles?.popoverMenuWrapper]} ref={outsideClickRef}>
          <div css={[styles.topBar, customStyles?.topBar]}>
            <Button onClick={handleSelectAll} variant='tertiary' disabled={disabled}>
              {t('Select all')}
            </Button>
            <Button onClick={handleClearAll} variant='tertiary' disabled={disabled}>
              {t('Clear')}
            </Button>
          </div>

          {showSearchBar && <SearchBarField searchFieldProps={searchFieldProps} cssStyle={styles.searchBar} />}

          {leafGroups.length > 0 && (
            <div css={styles.pinnedContent}>
              <GroupedChecklistContent
                checklistProps={checklistFieldProps}
                disabled={disabled}
                groups={leafGroups}
                groupStates={groupSelectionStates}
                label={label}
                name={`${label}-pinned`}
                onToggleGroup={handleToggleGroupSelection}
                showLabel={false}
                trackingIdBase={trackingId}
              />
            </div>
          )}
          {parentGroups.length > 0 && (
            <div
              css={[
                styles.scrollableContent,
                customStyles?.checkListWrapper,
                scrollableContentMaxHeight !== undefined &&
                  css`
                    max-height: ${scrollableContentMaxHeight}px;
                    overflow-y: scroll;
                    overflow-x: hidden;
                  `,
              ]}
            >
              <GroupedChecklistContent
                checklistProps={checklistFieldProps}
                disabled={disabled}
                groups={parentGroups}
                groupStates={groupSelectionStates}
                label={label}
                name={label}
                onToggleGroup={handleToggleGroupSelection}
                showLabel={false}
                trackingIdBase={trackingId}
              />
            </div>
          )}

          <Button
            css={[styles.applyButton, customStyles?.applyButton]}
            disabled={disabled}
            onClick={handleApplySelection}
            trackingId={trackingId ? `${trackingId}-apply-button` : undefined}
          >
            {t('Apply')}
          </Button>
        </div>
      </PopoverMenu>
    </div>
  );
}

const styles = {
  checklist: (showLabel?: boolean) => css`
    > label {
      display: ${showLabel ? 'block' : 'none'};
    }

    > div {
      align-items: center;
    }

    > div:last-of-type {
      margin-bottom: 0;
    }
  `,

  childCheckbox: css`
    margin: 0;
  `,

  childOption: (hideGroupLabel?: boolean) => css`
    display: flex;
    align-items: center;
    gap: ${theme.spacing(0.5)};
    padding: ${hideGroupLabel ? theme.spacing(1, 0) : theme.spacing(1, 1, 1, 2)};

    > div {
      width: 100%;

      > label {
        width: 100%;
      }
    }
  `,

  group: css`
    border-bottom: 1px solid ${theme.colors.neutral20};
    padding: ${theme.spacing(1, 2)};
    &:last-of-type {
      border-bottom: none;
    }
  `,

  groupCheckbox: css`
    margin: 0;
    align-items: center;
    width: 100%;
    > label {
      width: 100%;
    }
  `,

  groupCheckboxOption: css`
    margin: 0;
    > label {
      display: none;
    }
  `,

  groupHeader: (hasChildren: boolean) => css`
    position: sticky;
    top: 0;
    z-index: 1;
    background-color: ${theme.colors.white};
    display: flex;
    align-items: center;
    gap: ${theme.spacing(1)};
    margin-bottom: ${theme.spacing(hasChildren ? 1 : 0)};
    /* Extend background to cover the parent group's top and side padding so there's no gap behind the sticky header */
    padding-top: ${theme.spacing(1)};
    margin-top: -${theme.spacing(1)};
    margin-left: -${theme.spacing(2)};
    margin-right: -${theme.spacing(2)};
    padding-left: ${theme.spacing(2)};
    padding-right: ${theme.spacing(2)};
  `,

  chipInputContainer: {
    height: '100%',
    width: '100%',
  },

  chipInputWrapper: css`
    display: flex;
    flex-wrap: wrap;
    width: 100%;

    .chip {
      background-color: ${theme.colors.primary5};
      color: ${theme.font.colors.default};

      svg {
        color: ${theme.colors.primary90};
      }
    }
  `,

  fieldContainer: css`
    > div {
      height: 40px;
      padding: ${theme.spacing(1, 1)};
    }

    cursor: pointer;
  `,
  /******* */

  pinnedContent: css`
    flex-shrink: 0;
    border-bottom: 1px solid ${theme.colors.neutral20};
  `,

  floatingScrollableContent: css`
    overflow-y: scroll;
    overflow-x: hidden;
    flex: 1;
  `,
  searchBar: css`
    padding: ${theme.spacing(1, 2)};
  `,
  topBar: css`
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: ${theme.spacing(0.25, 2)};

    button {
      color: ${theme.colors.text.interactive};
    }
  `,
  checkListWrapper: css`
    display: flex;
    flex-direction: column;
    min-width: 220px;
    max-height: calc(100vh - 100px);
    overflow: hidden;
  `,
  scrollableContent: css`
    overflow: auto;
    flex: 1;
    min-height: 0;
  `,
  applyButton: css`
    margin: auto;
    width: calc(100% - ${theme.spacing(2)});
    bottom: 0;
    position: sticky;
  `,
};
